import { Link } from "wouter";
import { Plane, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/language";

const EFFECTIVE_DATE_EN = "April 6, 2026";
const EFFECTIVE_DATE_FR = "6 avril 2026";

export default function Terms() {
  const { language, setLanguage } = useLanguage();
  const isFr = language === "fr";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card px-6 py-4 flex items-center justify-between">
        <Link href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
          <div className="bg-primary/20 p-1.5 rounded-md">
            <Plane className="h-5 w-5 text-primary" />
          </div>
          <span className="font-bold tracking-tight text-primary font-mono">FLIGHT DECK</span>
        </Link>
        <div className="flex items-center gap-4">
          <div className="flex gap-4 text-xs font-mono text-muted-foreground">
            <Link href="/privacy" className="hover:text-foreground transition-colors">
              {isFr ? "Confidentialité" : "Privacy Policy"}
            </Link>
            <Link href="/terms" className="text-primary underline underline-offset-2">
              {isFr ? "Conditions" : "Terms of Service"}
            </Link>
            <Link href="/waiver" className="hover:text-foreground transition-colors">
              {isFr ? "Avertissement" : "Liability Waiver"}
            </Link>
          </div>
          <button
            onClick={() => setLanguage(isFr ? "en" : "fr")}
            className="text-xs font-mono font-semibold text-primary border border-primary/40 rounded px-2 py-0.5 hover:bg-primary/10 transition-colors"
          >
            {isFr ? "EN" : "FR"}
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-10">
        {isFr ? <TermsFr /> : <TermsEn />}

        <div className="border-t border-border pt-8 flex flex-wrap gap-4 text-xs font-mono text-muted-foreground">
          <Link href="/privacy" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Politique de confidentialité" : "Privacy Policy"}
          </Link>
          <Link href="/waiver" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Avertissement" : "Liability Waiver"}
          </Link>
          <Link href="/contact" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Assistance" : "Support"}
          </Link>
          <Link href="/" className="hover:text-foreground transition-colors underline underline-offset-2">
            {isFr ? "Retour à l'application" : "Back to App"}
          </Link>
          <span className="ml-auto">© {new Date().getFullYear()} Flight Deck CPL Exam Prep</span>
        </div>
      </div>
    </div>
  );
}

function TermsEn() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Terms of Service</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Effective Date: {EFFECTIVE_DATE_EN} &nbsp;·&nbsp; Governing Law: Province of Ontario, Canada
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        Flight Deck CPL Exam Prep is an independent educational preparation tool. It is not affiliated with, endorsed by, or operated by Transport Canada or any other government authority. Completing practice sessions on this platform does not guarantee success on any official Transport Canada examination or confer any licence, rating, or certification.
      </p>

      <Section title="1. Acceptance of Terms">
        <P>
          By registering for, accessing, or using Flight Deck CPL Exam Prep (the "Service"), you agree to be bound by these Terms of Service ("Terms") and our <Link href="/privacy" className="text-primary underline underline-offset-2">Privacy Policy</Link>. If you do not agree to these Terms, you must not use the Service.
        </P>
        <P>
          These Terms constitute a legally binding agreement between you and the operator of Flight Deck CPL Exam Prep ("we," "us," or "our"), governed by the laws of the Province of Ontario and the applicable federal laws of Canada.
        </P>
      </Section>

      <Section title="2. Description of Service">
        <P>
          Flight Deck CPL Exam Prep provides an online platform for individuals preparing for Canadian Commercial Pilot Licence (CPL) written examinations. The Service includes:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>A curated bank of practice questions across exam subject areas (General Knowledge, Air Law, Meteorology, Navigation, etc.).</li>
          <li>Timed and untimed practice session modes.</li>
          <li>Performance tracking, weakness analysis, and progress reporting.</li>
          <li>A community forum for discussion among registered users.</li>
          <li>A support ticket system for assistance.</li>
        </ul>
        <P>
          Access to the Service requires registration and approval by an administrator. We reserve the right to modify or discontinue any feature of the Service at any time, with or without notice.
        </P>
      </Section>

      <Section title="3. Educational Purpose — No Official Affiliation">
        <P>
          The Service is provided solely for educational and self-study purposes. Practice questions are created independently and are intended to help candidates familiarize themselves with examination topics and formats. By using the Service, you acknowledge and agree that:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Flight Deck CPL Exam Prep is <strong>not affiliated with, endorsed by, or approved by Transport Canada</strong>, the Civil Aviation Authority, or any government body.</li>
          <li>The practice questions are <strong>not official Transport Canada examination questions</strong> and are not guaranteed to reflect the current content, format, weighting, or difficulty of any official examination.</li>
          <li>Results achieved on this platform <strong>do not predict, guarantee, or imply</strong> success on any official Transport Canada examination.</li>
          <li>You remain solely responsible for ensuring you meet all Transport Canada requirements before attempting any official examination.</li>
          <li>All official aviation examinations must be scheduled and completed through Transport Canada's authorized processes.</li>
        </ul>
      </Section>

      <Section title="4. Eligibility and Account Registration">
        <P>
          To use the Service, you must be at least 16 years of age and capable of forming a binding contract under applicable Canadian law. By registering, you represent that the information you provide is accurate, complete, and current.
        </P>
        <P>
          You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You must notify us immediately through our <Link href="/contact" className="text-primary underline underline-offset-2">support form</Link> if you suspect unauthorized access to your account.
        </P>
        <P>
          Account access is subject to administrator approval and may be revoked at our sole discretion.
        </P>
      </Section>

      <Section title="5. Acceptable Use">
        <P>You agree not to:</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Share, resell, or distribute access to the Service or its content to third parties.</li>
          <li>Copy, reproduce, scrape, or systematically download question bank content for commercial purposes or distribution.</li>
          <li>Post content on the forum that is unlawful, defamatory, harassing, obscene, or otherwise objectionable.</li>
          <li>Attempt to reverse-engineer, hack, or compromise the security or integrity of the platform.</li>
          <li>Use automated bots, scripts, or tools to access or interact with the Service.</li>
          <li>Impersonate another user, administrator, or any other person.</li>
          <li>Use the Service for any purpose that violates applicable Canadian law.</li>
        </ul>
      </Section>

      <Section title="6. Intellectual Property">
        <P>
          All content on the Service — including but not limited to practice questions, explanations, design elements, and software — is owned by or licensed to Flight Deck CPL Exam Prep and is protected by Canadian copyright law (<em>Copyright Act</em>, R.S.C. 1985, c. C-42) and other applicable intellectual property laws.
        </P>
        <P>
          You are granted a limited, non-exclusive, non-transferable, revocable licence to access and use the Service for personal, non-commercial exam preparation purposes only. Nothing in these Terms transfers any intellectual property rights to you.
        </P>
      </Section>

      <Section title="7. User-Generated Content">
        <P>
          By posting content to the forum or submitting support tickets, you grant us a non-exclusive, royalty-free licence to use, display, and moderate that content as necessary to operate the Service. You are solely responsible for all content you post and must ensure it does not violate these Terms or any applicable law.
        </P>
        <P>
          We reserve the right to remove any user-generated content that we determine, in our sole discretion, to be in violation of these Terms or otherwise harmful to the community.
        </P>
      </Section>

      <Section title="8. Disclaimer of Warranties">
        <P>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, TO THE FULLEST EXTENT PERMITTED BY APPLICABLE CANADIAN LAW. WE EXPRESSLY DISCLAIM ALL WARRANTIES INCLUDING, WITHOUT LIMITATION:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>That the practice questions are accurate, current, complete, or representative of official Transport Canada examination content.</li>
          <li>That use of the Service will result in passing any official examination.</li>
          <li>That the Service will be uninterrupted, error-free, or free of viruses or other harmful components.</li>
        </ul>
      </Section>

      <Section title="9. Limitation of Liability">
        <P>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE CANADIAN LAW, IN NO EVENT SHALL FLIGHT DECK CPL EXAM PREP, ITS OPERATORS, EMPLOYEES, CONTRACTORS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Your use of or inability to use the Service.</li>
          <li>Failure to pass any official Transport Canada examination.</li>
          <li>Inaccuracies in practice question content.</li>
          <li>Unauthorized access to your account.</li>
        </ul>
        <P>
          Nothing in these Terms limits liability for fraud, death or personal injury caused by negligence, or any other liability that cannot be excluded or limited under applicable Canadian law (including the <em>Consumer Protection Act, 2002</em> (Ontario) where applicable).
        </P>
      </Section>

      <Section title="10. Indemnification">
        <P>
          You agree to indemnify, defend, and hold harmless Flight Deck CPL Exam Prep and its operators from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in connection with your use of the Service, your violation of these Terms, or your violation of any applicable law.
        </P>
      </Section>

      <Section title="11. Account Termination">
        <P>
          We reserve the right to suspend or terminate your account at any time, without notice, if we reasonably believe you have violated these Terms or engaged in conduct harmful to the platform or other users.
        </P>
        <P>
          You may request deletion of your account at any time through our <Link href="/contact" className="text-primary underline underline-offset-2">support ticket system</Link>. Upon account deletion, your personal information will be handled in accordance with our Privacy Policy.
        </P>
      </Section>

      <Section title="12. Governing Law and Dispute Resolution">
        <P>
          These Terms are governed by and construed in accordance with the laws of the Province of Ontario and the applicable federal laws of Canada, without regard to conflict of law principles.
        </P>
        <P>
          Any dispute arising from or relating to these Terms or the Service shall first be attempted to be resolved through good-faith negotiation. If unresolved, disputes shall be subject to the exclusive jurisdiction of the courts of the Province of Ontario.
        </P>
      </Section>

      <Section title="13. Changes to These Terms">
        <P>
          We reserve the right to modify these Terms at any time. When we make material changes, we will notify registered users by email or through a notice on the platform. Your continued use of the Service after any changes constitutes your acceptance of the revised Terms. The effective date at the top of this page will be updated accordingly.
        </P>
      </Section>

      <Section title="14. Scenario Training">
        <div className="p-3 mb-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs font-mono text-amber-400">
          ⚠ Scenario Training may be limited by plan and is subject to change as we improve the product.
        </div>
        <P>
          Flight Deck CPL Exam Prep may offer an optional <strong>Scenario Training</strong> mode with multi-topic, scenario-style practice questions. By using Scenario Training, you agree to the following additional terms, which form part of and are incorporated into these Terms of Service:
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Not Authoritative — Educational Use Only:</strong> Scenario questions are provided for supplementary practice only. They have not been verified, certified, or approved by Transport Canada or any aviation regulatory body. They are <strong>not</strong> official CPAER examination questions and may contain errors, inaccuracies, outdated information, or content that does not reflect current Canadian aviation regulations or examination standards.</li>
          <li><strong>No Guarantee of Accuracy:</strong> Flight Deck CPL Exam Prep makes no representation or warranty, express or implied, that any scenario question is accurate, complete, current, or suitable for any purpose. You assume all risk associated with relying on this content for your aviation studies.</li>
          <li><strong>Not a Substitute for Official Study Materials:</strong> Scenario questions must not be used as a replacement for official Transport Canada publications, approved ground school instruction, or any other authoritative aviation study resource. Always consult primary Canadian aviation sources.</li>
          <li><strong>Editorial Review:</strong> Content may be reviewed and curated by our team before or after publication. Even reviewed questions may contain errors. You should independently verify any information that may affect your aviation decision-making or safety.</li>
          <li><strong>No Extra Liability for Scenario Content:</strong> To the maximum extent permitted by applicable Canadian law, Flight Deck CPL Exam Prep, its operators, employees, and affiliates expressly disclaim all liability for any loss, damage, harm, or adverse outcome — whether direct, indirect, incidental, consequential, or otherwise — arising out of or in connection with your use of, or reliance on, scenario training content.</li>
          <li><strong>Aviation Safety:</strong> Practice content must never be used as a basis for actual flight operations, aeronautical decision-making, or any safety-critical aviation task. All flight operations must be conducted in accordance with current Canadian Aviation Regulations and applicable Transport Canada guidance.</li>
          <li><strong>Feature Changes:</strong> Scenario Training is provided on an "AS IS" and "AS AVAILABLE" basis without warranty of any kind. We reserve the right to modify, suspend, or permanently discontinue Scenario Training at any time, with or without notice, and without liability to you.</li>
          <li><strong>Intellectual Property:</strong> Scenario training content is owned by Flight Deck CPL Exam Prep. You are granted no rights to reproduce, distribute, or commercialize it except as part of normal personal use of the Service.</li>
        </ul>
        <P>
          By using Scenario Training, you acknowledge that you have read, understood, and agreed to these additional terms. If you do not agree, you must not use Scenario Training.
        </P>
      </Section>

      <Section title="15. Contact">
        <P>
          For any questions regarding these Terms of Service, please contact us:
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Legal</div>
          <div className="text-muted-foreground">Contact: <Link href="/contact" className="text-primary underline underline-offset-2">Submit a support ticket</Link></div>
        </div>
      </Section>
    </>
  );
}

function TermsFr() {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">Conditions d'utilisation</h1>
        </div>
        <p className="text-sm text-muted-foreground font-mono">
          Date d'entrée en vigueur : {EFFECTIVE_DATE_FR} &nbsp;·&nbsp; Droit applicable : Province de l'Ontario, Canada
        </p>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-border pl-4">
        Flight Deck CPL Exam Prep est un outil de préparation aux examens indépendant. Il n'est pas affilié à Transports Canada ni à toute autre autorité gouvernementale, et n'est ni approuvé ni exploité par ceux-ci. La réalisation de sessions de pratique sur cette plateforme ne garantit pas la réussite à un examen officiel de Transports Canada et ne confère aucune licence, qualification ou certification.
      </p>

      <Section title="1. Acceptation des conditions">
        <P>
          En vous inscrivant, en accédant ou en utilisant Flight Deck CPL Exam Prep (le « Service »), vous acceptez d'être lié par les présentes Conditions d'utilisation (les « Conditions ») et notre <Link href="/privacy" className="text-primary underline underline-offset-2">Politique de confidentialité</Link>. Si vous n'acceptez pas ces Conditions, vous ne devez pas utiliser le Service.
        </P>
        <P>
          Les présentes Conditions constituent un accord juridiquement contraignant entre vous et l'exploitant de Flight Deck CPL Exam Prep (« nous », « notre » ou « nos »), régi par les lois de la Province de l'Ontario et les lois fédérales applicables du Canada.
        </P>
      </Section>

      <Section title="2. Description du service">
        <P>
          Flight Deck CPL Exam Prep offre une plateforme en ligne aux personnes se préparant aux examens écrits pour la licence de pilote commercial (LPC) canadienne. Le Service comprend :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Une banque de questions de pratique couvrant les domaines d'examen (Connaissances générales, Réglementation aéronautique, Météorologie, Navigation, etc.).</li>
          <li>Des modes de sessions de pratique chronométrées et non chronométrées.</li>
          <li>Le suivi des performances, l'analyse des faiblesses et les rapports de progression.</li>
          <li>Un forum communautaire pour la discussion entre utilisateurs inscrits.</li>
          <li>Un système de billets d'assistance.</li>
        </ul>
        <P>
          L'accès au Service requiert une inscription et l'approbation d'un administrateur. Nous nous réservons le droit de modifier ou d'interrompre toute fonctionnalité du Service à tout moment, avec ou sans préavis.
        </P>
      </Section>

      <Section title="3. Vocation éducative — Aucune affiliation officielle">
        <P>
          Le Service est fourni uniquement à des fins éducatives et d'autoapprentissage. Les questions de pratique sont créées de manière indépendante et visent à aider les candidats à se familiariser avec les sujets et les formats d'examen. En utilisant le Service, vous reconnaissez et acceptez que :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Flight Deck CPL Exam Prep <strong>n'est pas affilié à, approuvé par ou associé à Transports Canada</strong>, l'autorité de l'aviation civile ou tout organisme gouvernemental.</li>
          <li>Les questions de pratique <strong>ne sont pas des questions officielles d'examen de Transports Canada</strong> et ne sont pas garanties de refléter le contenu, le format, la pondération ou la difficulté de tout examen officiel.</li>
          <li>Les résultats obtenus sur cette plateforme <strong>ne prédisent, ne garantissent ni n'impliquent</strong> la réussite à tout examen officiel de Transports Canada.</li>
          <li>Vous demeurez seul responsable de vous assurer que vous satisfaites à toutes les exigences de Transports Canada avant de tenter un examen officiel.</li>
          <li>Tous les examens officiels d'aviation doivent être planifiés et passés via les processus autorisés de Transports Canada.</li>
        </ul>
      </Section>

      <Section title="4. Admissibilité et inscription">
        <P>
          Pour utiliser le Service, vous devez avoir au moins 16 ans et être en mesure de conclure un contrat contraignant en vertu du droit canadien applicable. En vous inscrivant, vous déclarez que les renseignements fournis sont exacts, complets et à jour.
        </P>
        <P>
          Vous êtes responsable de la confidentialité de vos identifiants de connexion et de toute activité effectuée sur votre compte. Vous devez nous notifier immédiatement via notre <Link href="/contact" className="text-primary underline underline-offset-2">formulaire d'assistance</Link> si vous soupçonnez un accès non autorisé à votre compte.
        </P>
        <P>
          L'accès au compte est soumis à l'approbation de l'administrateur et peut être révoqué à notre seule discrétion.
        </P>
      </Section>

      <Section title="5. Utilisation acceptable">
        <P>Vous vous engagez à ne pas :</P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Partager, revendre ou distribuer l'accès au Service ou à son contenu à des tiers.</li>
          <li>Copier, reproduire, extraire ou télécharger systématiquement le contenu de la banque de questions à des fins commerciales ou de distribution.</li>
          <li>Publier sur le forum des contenus illégaux, diffamatoires, harcelants, obscènes ou autrement répréhensibles.</li>
          <li>Tenter de décompiler, pirater ou compromettre la sécurité ou l'intégrité de la plateforme.</li>
          <li>Utiliser des robots, scripts ou outils automatisés pour accéder au Service ou interagir avec celui-ci.</li>
          <li>Usurper l'identité d'un autre utilisateur, d'un administrateur ou de toute autre personne.</li>
          <li>Utiliser le Service à des fins contraires au droit canadien applicable.</li>
        </ul>
      </Section>

      <Section title="6. Propriété intellectuelle">
        <P>
          Tout le contenu du Service — y compris, sans s'y limiter, les questions de pratique, les explications, les éléments de conception et le logiciel — est la propriété de Flight Deck CPL Exam Prep ou lui est sous licence, et est protégé par la loi canadienne sur le droit d'auteur (<em>Loi sur le droit d'auteur</em>, L.R.C. 1985, ch. C-42) et les autres lois applicables en matière de propriété intellectuelle.
        </P>
        <P>
          Une licence limitée, non exclusive, non cessible et révocable vous est accordée pour accéder au Service et l'utiliser à des fins personnelles et non commerciales de préparation aux examens uniquement. Rien dans les présentes Conditions ne transfère de droits de propriété intellectuelle.
        </P>
      </Section>

      <Section title="7. Contenu généré par les utilisateurs">
        <P>
          En publiant du contenu sur le forum ou en soumettant des billets d'assistance, vous nous accordez une licence non exclusive et libre de redevances pour utiliser, afficher et modérer ce contenu dans la mesure nécessaire au fonctionnement du Service. Vous êtes seul responsable de tout le contenu que vous publiez et devez vous assurer qu'il ne contrevient pas aux présentes Conditions ni à toute loi applicable.
        </P>
        <P>
          Nous nous réservons le droit de supprimer tout contenu généré par les utilisateurs que nous estimons, à notre seule discrétion, contraire aux présentes Conditions ou préjudiciable à la communauté.
        </P>
      </Section>

      <Section title="8. Exclusion de garanties">
        <P>
          LE SERVICE EST FOURNI « TEL QUEL » ET « SELON DISPONIBILITÉ » SANS GARANTIE D'AUCUNE SORTE, EXPRESSE OU IMPLICITE, DANS TOUTE LA MESURE PERMISE PAR LE DROIT CANADIEN APPLICABLE. NOUS DÉCLINONS EXPRESSÉMENT TOUTES LES GARANTIES, Y COMPRIS, SANS LIMITATION :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Que les questions de pratique sont exactes, actuelles, complètes ou représentatives du contenu officiel des examens de Transports Canada.</li>
          <li>Que l'utilisation du Service se traduira par la réussite à un examen officiel.</li>
          <li>Que le Service sera ininterrompu, exempt d'erreurs ou exempt de virus ou d'autres composants nuisibles.</li>
        </ul>
      </Section>

      <Section title="9. Limitation de responsabilité">
        <P>
          DANS TOUTE LA MESURE PERMISE PAR LE DROIT CANADIEN APPLICABLE, FLIGHT DECK CPL EXAM PREP, SES EXPLOITANTS, EMPLOYÉS, SOUS-TRAITANTS OU AFFILIÉS NE SERONT EN AUCUN CAS RESPONSABLES DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS DÉCOULANT DE OU EN RAPPORT AVEC :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li>Votre utilisation du Service ou l'impossibilité d'y accéder.</li>
          <li>L'échec à un examen officiel de Transports Canada.</li>
          <li>Des inexactitudes dans le contenu des questions de pratique.</li>
          <li>Un accès non autorisé à votre compte.</li>
        </ul>
        <P>
          Rien dans les présentes Conditions ne limite la responsabilité pour fraude, décès ou préjudice corporel causé par négligence, ou toute autre responsabilité qui ne peut être exclue ou limitée en vertu du droit canadien applicable (y compris la <em>Loi de 2002 sur la protection du consommateur</em> (Ontario) le cas échéant).
        </P>
      </Section>

      <Section title="10. Indemnisation">
        <P>
          Vous acceptez d'indemniser, de défendre et de dégager de toute responsabilité Flight Deck CPL Exam Prep et ses exploitants contre toute réclamation, responsabilité, dommage, perte et dépense (y compris les honoraires juridiques raisonnables) découlant de ou en rapport avec votre utilisation du Service, votre violation des présentes Conditions ou votre violation de tout droit applicable.
        </P>
      </Section>

      <Section title="11. Résiliation de compte">
        <P>
          Nous nous réservons le droit de suspendre ou de résilier votre compte à tout moment, sans préavis, si nous estimons raisonnablement que vous avez contrevenu aux présentes Conditions ou adopté un comportement préjudiciable à la plateforme ou aux autres utilisateurs.
        </P>
        <P>
          Vous pouvez demander la suppression de votre compte à tout moment via notre <Link href="/contact" className="text-primary underline underline-offset-2">système de billets d'assistance</Link>. Lors de la suppression du compte, vos renseignements personnels seront traités conformément à notre Politique de confidentialité.
        </P>
      </Section>

      <Section title="12. Droit applicable et règlement des différends">
        <P>
          Les présentes Conditions sont régies et interprétées conformément aux lois de la Province de l'Ontario et aux lois fédérales applicables du Canada, sans égard aux principes de conflits de lois.
        </P>
        <P>
          Tout différend découlant des présentes Conditions ou du Service devra d'abord faire l'objet d'une tentative de résolution de bonne foi. À défaut de résolution, les différends seront soumis à la juridiction exclusive des tribunaux de la Province de l'Ontario.
        </P>
      </Section>

      <Section title="13. Modifications des présentes Conditions">
        <P>
          Nous nous réservons le droit de modifier les présentes Conditions à tout moment. Lorsque nous apportons des modifications importantes, nous en informerons les utilisateurs inscrits par courriel ou par un avis sur la plateforme. Votre utilisation continue du Service après tout changement constitue votre acceptation des Conditions révisées. La date d'entrée en vigueur en haut de cette page sera mise à jour en conséquence.
        </P>
      </Section>

      <Section title="14. Entraînement par scénarios">
        <div className="p-3 mb-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs font-mono text-amber-400">
          ⚠ L'entraînement par scénarios peut être limité selon le forfait et est sujet à des changements à mesure que nous améliorons le produit.
        </div>
        <P>
          Flight Deck CPL Exam Prep peut offrir un mode facultatif d'<strong>entraînement par scénarios</strong> avec des questions de pratique de type scénario et multi-sujets. En utilisant l'entraînement par scénarios, vous acceptez les conditions supplémentaires suivantes, qui font partie intégrante des présentes Conditions d'utilisation :
        </P>
        <ul className="list-disc pl-6 space-y-2 text-xs text-foreground/80">
          <li><strong>Non officielles — Usage éducatif uniquement :</strong> Les questions de scénario sont fournies à des fins de pratique supplémentaire uniquement. Elles n'ont pas été vérifiées, certifiées ni approuvées par Transports Canada ou tout organisme de réglementation de l'aviation. Elles ne constituent <strong>pas</strong> des questions officielles d'examen du CPAER et peuvent contenir des erreurs, des inexactitudes, des informations périmées ou un contenu ne reflétant pas les normes actuelles de la réglementation canadienne de l'aviation.</li>
          <li><strong>Aucune garantie d'exactitude :</strong> Flight Deck CPL Exam Prep ne fait aucune déclaration ni garantie, expresse ou implicite, quant à l'exactitude, l'intégralité, l'actualité ou la pertinence de toute question de scénario. Vous assumez tous les risques associés à l'utilisation de ce contenu pour vos études en aviation.</li>
          <li><strong>Ne remplace pas les documents officiels :</strong> Les questions de scénario ne doivent pas être utilisées en remplacement des publications officielles de Transports Canada, des cours au sol approuvés ou de toute autre ressource d'étude officielle en aviation. Consultez toujours les sources primaires canadiennes en aviation.</li>
          <li><strong>Révision éditoriale :</strong> Le contenu peut être révisé et conservé par notre équipe avant ou après publication. Même les questions révisées peuvent contenir des erreurs. Vous devez vérifier indépendamment toute information susceptible d'affecter votre prise de décision en aviation ou votre sécurité.</li>
          <li><strong>Responsabilité limitée pour le contenu de scénario :</strong> Dans toute la mesure permise par le droit canadien applicable, Flight Deck CPL Exam Prep, ses exploitants, employés et affiliés déclinent expressément toute responsabilité pour toute perte, dommage, préjudice ou résultat défavorable découlant de ou en rapport avec votre utilisation du contenu d'entraînement par scénarios.</li>
          <li><strong>Sécurité aéronautique :</strong> Le contenu de pratique ne doit jamais servir de base pour des opérations de vol réelles, la prise de décision aéronautique ou toute tâche d'aviation critique pour la sécurité. Toutes les opérations de vol doivent être menées conformément au Règlement de l'aviation canadien en vigueur.</li>
          <li><strong>Évolution de la fonctionnalité :</strong> L'entraînement par scénarios est fourni « tel quel » et « selon disponibilité » sans garantie d'aucune sorte. Nous nous réservons le droit de modifier, suspendre ou interrompre définitivement l'entraînement par scénarios à tout moment, sans préavis ni responsabilité envers vous.</li>
          <li><strong>Propriété intellectuelle :</strong> Le contenu d'entraînement par scénarios appartient à Flight Deck CPL Exam Prep. Aucun droit de le reproduire, le distribuer ou le commercialiser ne vous est accordé, sauf dans le cadre d'une utilisation personnelle normale du Service.</li>
        </ul>
        <P>
          En utilisant l'entraînement par scénarios, vous reconnaissez avoir lu, compris et accepté ces conditions supplémentaires. Si vous n'acceptez pas, vous ne devez pas utiliser ce mode.
        </P>
      </Section>

      <Section title="15. Nous contacter">
        <P>
          Pour toute question concernant les présentes Conditions d'utilisation, veuillez nous contacter :
        </P>
        <div className="bg-card border border-border rounded-md p-4 font-mono text-xs space-y-1">
          <div className="font-semibold text-foreground">Flight Deck CPL Exam Prep — Service juridique</div>
          <div className="text-muted-foreground">Contact : <Link href="/contact" className="text-primary underline underline-offset-2">Soumettre un billet d'assistance</Link></div>
        </div>
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-bold text-foreground border-b border-border pb-2">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-foreground/80 leading-relaxed">{children}</p>;
}
